
export enum Role {
    Admin = 'Admin',
    Manager = 'Manager',
    Doctor = 'Doctor',
    Nurse = 'Nurse',
    Paramedic = 'Paramedic',
    EMT = 'EMT',
    FREC4 = 'FREC4',
    FREC3 = 'FREC3',
    FirstAider = 'FirstAider',
    Welfare = 'Welfare',
    Pending = 'Pending'
}

export interface AppNotification {
    id: string;
    userId: string;
    title: string;
    message: string;
    type: 'info' | 'alert' | 'success';
    read: boolean;
    timestamp: string;
    link?: string;
}

export interface ComplianceDoc {
    id: string;
    name: string;
    expiryDate: string;
    status: 'Valid' | 'Expiring' | 'Expired' | 'Pending' | 'Rejected';
    fileUrl?: string;
    uploadedAt?: string;
}

export type WorkforceEmploymentType = 'PAYE' | 'Contractor' | 'SubContractor' | 'Bank';

export interface WorkforceRoleHistoryEntry {
    role: Role;
    startedAt: string;
    endedAt?: string;
    changedBy?: string;
}

export interface OnboardingTask {
    id: string;
    title: string;
    ownerRole: 'Recruitment' | 'Manager' | 'Admin' | 'HR';
    status: 'Pending' | 'Completed';
    createdAt: string;
    dueAt?: string;
    completedAt?: string;
}

export interface IdentityMergeStrategyRecord {
    strategy: 'LinkExistingByEmail' | 'ProvisionNewProfile';
    authPrincipalEmail: string;
    staffUserId: string;
    applicationId: string;
    mergedAt: string;
    mergedByUid: string;
    mergedByName: string;
    notes?: string;
}

export interface WorkforceLifecycleCase {
    id: string;
    type: 'Probation' | 'Appraisal' | 'Disciplinary' | 'Leaver';
    status: 'Open' | 'InReview' | 'Closed';
    ownerRole: 'HR' | 'Manager';
    summary: string;
    createdAt: string;
    dueAt?: string;
    closedAt?: string;
    updatedAt?: string;
}

export interface User {
    uid: string;
    email: string;
    name: string;
    tenantId?: string;
    tenantName?: string;
    role: Role;
    status: 'Active' | 'Pending' | 'Suspended' | 'Rejected';
    regNumber?: string;
    photoURL?: string;
    compliance?: ComplianceDoc[];
    pin?: string;
    pinHash?: string;
    pinLastUpdated?: string;
    employeeId?: string;
    employmentType?: WorkforceEmploymentType;
    primaryBranch?: string;
    secondaryBranches?: string[];
    phone?: string;
    address?: string;
    roleHistory?: WorkforceRoleHistoryEntry[];
    source?: 'RecruitmentConversion' | 'SelfSignup' | 'AdminCreated';
    convertedFromApplicationId?: string;
    convertedAt?: string;
    plannedStartDate?: string;
    onboardingTasks?: OnboardingTask[];
    workforceLifecycleCases?: WorkforceLifecycleCase[];
    identityMerge?: IdentityMergeStrategyRecord;
    createdAt?: string;
    updatedAt?: string;
    stats?: {
        totalHours: number;
        completedShifts: number;
        lastShiftDate: string;
    };
    roleChangeRequest?: {
        newRole: string;
        reason: string;
        status: string;
        requestDate: string;
    };
    /** Custom role group assigned by an admin — overrides display label while preserving base role for hierarchy. */
    roleGroupId?: string;
}

export type PermissionMatrixModule =
    | 'Dashboard'
    | 'ClinicalOps'
    | 'Dispatch'
    | 'Rota'
    | 'AssetsAndDrugs'
    | 'DocumentsAndGovernance'
    | 'PeopleAndRecruitment'
    | 'Administration';

export type PermissionMatrixAction =
    | 'View'
    | 'CreateOrEdit'
    | 'ApproveOrPublish'
    | 'ExportOrFinance'
    | 'Administer';

export interface TenantPermissionMatrixRule {
    id: string;
    module: PermissionMatrixModule;
    action: PermissionMatrixAction;
    description: string;
    grants: Record<string, boolean>;
}

export interface TenantPermissionMatrix {
    id?: string;
    tenantId: string;
    version: number;
    status: 'Draft' | 'Active';
    rules: TenantPermissionMatrixRule[];
    updatedAt: string;
    updatedBy: string;
    updatedByName?: string;
}

export interface AccessRecertificationCampaign {
    id: string;
    tenantId: string;
    title: string;
    status: 'Open' | 'Closed';
    matrixVersion: number;
    windowStart: string;
    dueDate: string;
    createdAt: string;
    createdBy: string;
    createdByName?: string;
    closedAt?: string;
    closedBy?: string;
}

export interface AccessRecertificationAttestation {
    id: string;
    tenantId: string;
    campaignId: string;
    userId: string;
    userName: string;
    userRole: Role;
    notes?: string;
    submittedAt: string;
    submittedAtServer?: any;
}

export type JobPostingStatus = 'Draft' | 'Published' | 'Closed';

export interface JobPosting {
    id: string;
    tenantId: string;
    title: string;
    location: string;
    employmentType: 'PAYE' | 'SelfEmployed' | 'SubContractor' | 'Mixed';
    workPattern?: string;
    description: string;
    requirements: string[];
    status: JobPostingStatus;
    publishedAt?: string;
    createdAt: string;
    updatedAt: string;
    createdBy?: string;
}

export type ApplicationStatus =
    | 'Applied'
    | 'Screening'
    | 'ReferenceChecks'
    | 'InterviewInvited'
    | 'InterviewBooked'
    | 'OfferExtended'
    | 'Onboarding'
    | 'Hired'
    | 'Rejected';

export interface ApplicantReference {
    id: string;
    name: string;
    role: string;
    organisation?: string;
    email: string;
    phone?: string;
    status: 'Pending' | 'Requested' | 'Received' | 'Rejected';
    requestedAt?: string;
    respondedAt?: string;
    notes?: string;
}

export interface ApplicantDocument {
    id: string;
    name: string;
    url: string;
    category: 'CV' | 'Certification' | 'ID' | 'Reference' | 'Other';
    uploadedAt: string;
}

export interface ApplicationStatusEvent {
    id: string;
    status: ApplicationStatus;
    message: string;
    actor: 'Applicant' | 'Recruiter' | 'System';
    createdAt: string;
}

export interface ApplicationCommunication {
    id: string;
    channel: 'Portal' | 'Email';
    direction: 'Outbound' | 'Inbound';
    type: 'InterviewInvite' | 'ReferenceRequest' | 'Offer' | 'Rejection' | 'General';
    subject: string;
    message: string;
    createdAt: string;
    createdBy: string;
}

export interface InterviewSlot {
    id: string;
    tenantId: string;
    startsAt: string;
    endsAt: string;
    locationType: 'Video' | 'InPerson' | 'Phone';
    locationInfo?: string;
    interviewerName: string;
    status: 'Open' | 'Requested' | 'Booked' | 'Cancelled';
    bookedByApplicationId?: string;
}

export interface ApplicantProfile {
    fullName: string;
    email: string;
    phone: string;
    address?: string;
    roleAppliedFor?: string;
    yearsExperience?: number;
    summary?: string;
}

export interface ApplicationRecord {
    id: string;
    tenantId: string;
    jobPostingId?: string;
    portalAccessKey: string;
    applicant: ApplicantProfile;
    status: ApplicationStatus;
    references: ApplicantReference[];
    documents: ApplicantDocument[];
    requestedInterviewSlotId?: string;
    confirmedInterviewSlotId?: string;
    onboardingUserId?: string;
    onboardingConvertedAt?: string;
    identityMerge?: IdentityMergeStrategyRecord;
    statusTimeline: ApplicationStatusEvent[];
    communicationLog?: ApplicationCommunication[];
    recruiterNotes?: string;
    invitedForInterview?: boolean;
    createdAt: string;
    updatedAt: string;
    lastApplicantUpdateAt?: string;
}

export interface ShiftSlot {
    id: string;
    role: Role;
    userId?: string;
    userName?: string;
    bids?: ShiftBid[];
}

export interface ShiftBid {
    userId: string;
    userName: string;
    userRole: string;
    timestamp: string;
}

export interface TimeRecord {
    userId: string;
    clockInTime?: string;
    clockInLocation?: string;
    clockOutTime?: string;
    durationMinutes?: number;
    approvalStatus?: 'Pending' | 'Approved' | 'Rejected';
    approvedAt?: string;
    approvedBy?: string;
    approvedByName?: string;
    approvalNotes?: string;
}

export interface ShiftResource {
    id: string;
    type: 'Vehicle' | 'Kit';
    name: string;
}

export interface ShiftOrgContext {
    trust?: string;
    division?: string;
    station?: string;
    team?: string;
}

export interface ShiftDemandTemplate {
    id: string;
    name: string;
    location: string;
    durationHours: number;
    slots: Array<{ role: Role }>;
    tags?: string[];
    orgContext?: ShiftOrgContext;
}

export interface Shift {
    id: string;
    tenantId?: string;
    start: Date;
    end: Date;
    location: string;
    orgContext?: ShiftOrgContext;
    address?: string;
    notes?: string;
    status: 'Open' | 'Filled' | 'Cancelled';
    tags?: string[];
    slots: ShiftSlot[];
    timeRecords?: Record<string, TimeRecord>;
    resources?: ShiftResource[];
    createdBy?: string;
}

export interface Announcement {
    id: string;
    title: string;
    message: string;
    priority: 'Normal' | 'Urgent';
    date: any;
    author: string;
    readBy: string[];
}

export interface Vehicle {
    id: string;
    tenantId?: string;
    callSign: string;
    registration: string;
    type: string;
    status: string;
    mileage?: number;
    checklist?: ChecklistItem[];
    lastCheck?: string;
    lastCheckResult?: 'Pass' | 'Fail';
    correctiveActionRequired?: boolean;
    correctiveActionNotes?: string;
    correctiveActionResolvedAt?: string;
    correctiveActionResolvedBy?: string;
}

export interface MedicalKit {
    id: string;
    tenantId?: string;
    name: string;
    type: string;
    status: string;
    lastCheck?: string;
    earliestExpiry?: string;
    checklist?: ChecklistItem[];
    lastCheckResult?: 'Pass' | 'Fail';
    correctiveActionRequired?: boolean;
    correctiveActionNotes?: string;
    correctiveActionResolvedAt?: string;
    correctiveActionResolvedBy?: string;
}

export interface ChecklistItem {
    id: string;
    label: string;
    category: string;
}

export interface AssetCheck {
    id: string;
    tenantId?: string;
    assetId: string;
    assetType: 'Vehicle' | 'Kit';
    userId: string;
    userName: string;
    timestamp: string;
    status: 'Pass' | 'Fail';
    faults?: string[];
    checklistData?: Record<string, boolean>;
    correctiveActionRequired?: boolean;
    correctiveActionNotes?: string;
}

export interface MajorIncidentReport {
    id: string;
    tenantId?: string;
    active: boolean;
    declaredBy: string;
    declaredByRole?: string;
    timeDeclared: string;
    type: 'DECLARATION' | 'METHANE_REPORT';
    linkedShiftId?: string;
    methane: {
        majorIncidentDeclared: boolean;
        exactLocation: string;
        typeOfIncident: string;
        hazards: string;
        access: string;
        numberOfCasualties: string;
        emergencyServices: string;
    };
}

export interface Unavailability {
    id: string;
    tenantId?: string;
    userId: string;
    start: string;
    end: string;
    reason?: string;
    type: 'Holiday' | 'Sick' | 'Other';
}

export interface CPDEntry {
    id: string;
    title: string;
    date: string;
    type: 'Self-directed' | 'Work-based' | 'Formal';
    hours: number;
    reflection: string;
    evidenceUrl?: string;
    timestamp: any;
}

export interface Kudos {
    id: string;
    fromUser: string;
    fromUid: string;
    toUser: string;
    toUid: string;
    message: string;
    isPublic: boolean;
    timestamp: any;
    tags?: string[];
}

export interface InjuryMark {
    id: string;
    x: number;
    y: number;
    view: 'Anterior' | 'Posterior';
    type: 'Injury' | 'Pain' | 'IV' | 'IO' | 'IM' | 'SC' | 'Other';
    subtype?: string;
    location?: string;
    device?: string;
    gauge?: string;
    time?: string;
    success?: boolean;
    attempts?: number;
    notes?: string;
}

export interface VitalsEntry {
    id?: string;
    time: string;
    hr?: number;
    rr?: number;
    spo2?: number;
    bpSystolic?: number;
    bpDiastolic?: number;
    news2Score: number;
    temp?: number;
    oxygen: boolean;
    oxygenDevice?: string;
    oxygenFlow?: string;
    avpu?: 'A' | 'V' | 'P' | 'U';
    gcs?: number;
    bloodGlucose?: number;
    painScore?: number;
    popsScore?: number;
    bpPosition?: string;
    bpLimb?: string;
    rrRefused?: boolean;
    spo2Refused?: boolean;
    hrRefused?: boolean;
    bpRefused?: boolean;
    tempRefused?: boolean;
    gcsRefused?: boolean;
    bloodGlucoseRefused?: boolean;
    painScoreRefused?: boolean;
}

export interface DrugAdministration {
    id: string;
    time: string;
    drugName: string;
    dose: string;
    route: string;
    batchNumber?: string;
    authorisation: string;
    administeredBy: string;
    witnessedBy?: string;
    witnessUid?: string;
    witnessToken?: string;
}

export interface Procedure {
    id: string;
    time: string;
    type: string;
    site?: string;
    size?: string;
    success: boolean;
    attempts?: number;
    performedBy: string;
    details?: string;
    etco2?: string;
    depth?: string;
    secureMethod?: string;
}

export interface LinkedRecord {
    id: string;
    incidentNumber: string;
    date: string;
    diagnosis: string;
}

export interface BirthEpisode {
    episodeId: string;
    role?: 'Mother' | 'Neonate';
    motherDraftId?: string;
    neonateDraftIds: string[];
    expectedBabies?: number;
    deliveredBabies?: number;
    stage?: 'Labour' | 'Delivery' | 'Postnatal';
    deliveryMode?: 'Spontaneous' | 'Assisted' | 'Caesarean' | 'Water' | 'Unknown';
    placentaDelivered?: boolean;
    cordClamped?: boolean;
    apgar?: {
        oneMinute?: number;
        fiveMinutes?: number;
        tenMinutes?: number;
    };
    maternalComplications?: string;
    neonatalComplications?: string;
}

export interface DeathVerification {
    method: 'VOD' | 'ROLE';
    expectedDeath: boolean;
    verifiedBy: string;
    verifiedAt: string;
    observationMinutes?: number;
    criteriaMet: string[];
    coronerReferralRequired?: boolean;
    coronerReference?: string;
    notes?: string;
}

export interface WoundAssessment {
    id: string;
    site: string;
    classification: string;
    dimensions: string;
    contamination: string;
    tetanusStatus: string;
    closure?: string;
}

export interface ResusEvent {
    id: string;
    timestamp: string;
    action: string;
    type: 'Shock' | 'Drug' | 'Airway' | 'Mechanical' | 'Status' | 'Other';
    user: string;
}

export interface MediaAttachment {
    id: string;
    type: 'Photo';
    url: string;
    localUrl?: string;
    timestamp: string;
    notes?: string;
}

export interface CranialNerveStatus {
    nerve: string;
    test: string;
    status: 'Normal' | 'Abnormal' | 'Not Tested';
    notes?: string;
}

export interface CompanyDocument {
    id: string;
    title: string;
    category: 'Policy' | 'Clinical' | 'Operational' | 'HR' | 'Memo';
    description?: string;
    version: string;
    url: string;
    lastUpdated: string;
    uploadedBy: string;
    lifecycleStatus?: 'Draft' | 'PendingApproval' | 'Active' | 'Superseded';
    submittedForApprovalAt?: string;
    submittedForApprovalBy?: string;
    approvedAt?: string;
    approvedBy?: string;
    supersededAt?: string;
    supersededBy?: string;
    supersededByDocumentId?: string;
    acknowledgementCount?: number;
    lastAcknowledgedAt?: string;
}

export interface TraumaTriageResult {
    physiology: boolean;
    anatomy: boolean;
    mechanism: boolean;
    special: boolean;
    isMajorTrauma: boolean;
    criteria: string[];
    vehiclePosition?: string;
    estSpeed?: string;
    seatbeltWorn?: string;
    airbagsDeployed?: string;
    extrication?: string;
    mechanismDetails?: string; 
    rtcType?: string;
    intrusionOver30cm?: boolean;
    steeringWheelDeformed?: boolean;
    ejection?: boolean;
    crushEntrapmentMinutes?: string;
    compartmentSyndromeConcern?: boolean;
    suspensionHarnessTime?: string;
    suspensionSyncopeOnRelease?: boolean;
    temperatureStage?: string;
    penetratingEntranceFindings?: string;
    penetratingExitFindings?: string;
    muzzleFlashBurns?: boolean;
    blastOverpressureSigns?: string;
}

export interface NeuroAssessment {
    gcs: { eyes?: number; verbal?: number; motor?: number; total?: number };
    pupils: { leftSize?: number; leftReaction?: string; rightSize?: number; rightReaction?: string };
    fast: { face: string | null; arms: string | null; speech: string | null; time: string; testPositive: boolean };
    delirium4atScore?: string;
    lvoScreenTool?: 'ROSIER' | 'RACE' | 'None' | '';
    lvoScreenScore?: string;
    mimicExclusions?: string;
    seizureClassification?: string;
    seizureDurationMinutes?: string;
    seizureCount24h?: string;
    postIctalState?: boolean;
    headacheSnnoop10?: string;
    meningococcalWarningSigns?: string;
    lastKnownWell?: string;
    seizureActivity?: boolean;
    neuroRedFlags?: string;
    limbs: {
        leftArm: { power: string; sensation: string };
        rightArm: { power: string; sensation: string };
        leftLeg: { power: string; sensation: string };
        rightLeg: { power: string; sensation: string };
    };
    cranialNerves?: CranialNerveStatus[];
}

export interface MentalHealthAssessment {
    appearance: string;
    appearanceMarkers?: string[];
    behaviour: string;
    behaviourMarkers?: string[];
    speech: string;
    speechPattern?: string;
    mood: string;
    affect?: string;
    thoughtContent?: string;
    perceptionDisturbance?: string;
    safeguardingNeeds?: string;
    intoxicationConcern?: boolean;
    perception?: string;
    riskToSelf: boolean;
    riskToOthers: boolean;
    capacityStatus?: string; 
    selfHarmHistory?: string; 
    suicideRisk?: string; 
    currentIdeation?: 'Active' | 'Passive' | 'None' | '';
    planMeansAvailable?: boolean;
    preparationsMade?: string;
    protectiveFactors?: string;
    suicideIntentPlan?: string;
    substanceType?: string;
    ingestionRoute?: string;
    ingestionTime?: string;
    estimatedDose?: string;
    overdosePoisoningDetails?: string;
    toxidromeScreen?: string;
    behaviouralDisturbanceRedFlags?: string;
    deEscalationStatus?: 'None' | 'Verbal de-escalation successful' | 'Ongoing agitation' | '';
    deEscalationPlan?: string;
    mentalHealthHistory?: string; 
}

export type AuditEventType =
    | 'ClinicalAction'
    | 'DecisionAction'
    | 'ReferralAction'
    | 'AdminAction'
    | 'SystemAction';

export interface AuditTrailEntry {
    timestamp: string;
    action: string;
    details: string;
    user: string;
    eventType?: AuditEventType;
    taxonomy?: string;
    legalNarrative?: string;
}

export interface PrimarySurvey {
    danger?: { present?: boolean; details: string };
    response?: { avpu: string };
    catastrophicHaemorrhage?: boolean;
    catastrophicHaemorrhageStatus?: string;
    catastrophicHaemorrhageSource?: string;
    airway: { status: string; patency?: string; notes: string; intervention: string };
    breathing: {
        rate: string | number;
        effort: string;
        oxygenSats: string | number;
        chestExpansion?: string;
        airEntryL: string;
        airEntryR: string;
        soundsL: string;
        soundsR: string;
        rhythm: string;
        depth: string;
        respirations?: string;
        workOfBreathing?: string;
        chestMovement?: string;
    };
    circulation: {
        radialPulse: string;
        character?: string;
        skin: string;
        capRefill: string;
        color: string;
        systolicBP: string | number;
        diastolicBP: string | number;
        temp: string | number;
        radialStrength?: string;
        centralPulse?: string;
        skinState?: string;
        capillaryRefillCategory?: string;
    };
    disability: {
        avpu: string;
        gcs: string;
        pupils: string;
        bloodGlucose: string | number;
        grossMotor?: string;
        fastFace?: string;
        fastArms?: string;
        fastSpeech?: string;
        fastResult?: string;
    };
    exposure: {
        injuriesFound?: boolean;
        rash?: boolean;
        temp: string | number;
        grossFindings?: string;
        environment?: string;
    };
    airwaySounds?: string;
    cSpineRisk?: string;
    outcome?: { timeCriticalIndicator: string; notes: string };
}

export type AssessmentSectionId =
    | 'tab0'
    | 'tab1'
    | 'tab2'
    | 'tab3'
    | 'tab4'
    | 'tab5'
    | 'tab6'
    | 'tab7'
    | 'tab8'
    | 'tab9'
    | 'tab10';

export interface AssessmentInteraction {
    touchedSections: AssessmentSectionId[];
    lastTouchedSectionId?: AssessmentSectionId;
    lastTouchedAt?: string;
}

export interface EPRF {
    id: string;
    tenantId?: string;
    incidentNumber: string;
    callSign: string;
    location: string;
    mode: 'Clinical' | 'Welfare' | 'Minor';
    status: 'Draft' | 'Submitted';
    userId: string;
    accessUids?: string[];
    lastUpdated: string;
    lastSync?: string;
    shiftId?: string;
    assistingClinicians?: {
        id?: string;
        name: string;
        role: string;
        badgeNumber: string;
    }[];
    times: {
        incidentDate: string;
        callReceived: string;
        mobile: string;
        onScene: string;
        patientContact: string;
        departScene: string;
        atHospital: string;
        clear: string;
    };
    patient: {
        firstName: string;
        lastName: string;
        dob: string;
        gender: string;
        nhsNumber: string;
        address: string;
        postcode: string;
        contactNumber?: string;
        ethnicity?: string;
        nextOfKin?: {
            name: string;
            relationship: string;
            contactNumber: string;
        };
        chronicHypoxia: boolean;
        dnacpr: {
            hasDNACPR: boolean;
            dateIssued?: string;
            verified: boolean;
        };
    };
    history: {
        presentingComplaint: string;
        historyOfPresentingComplaint: string;
        sample?: {
            symptoms: string;
            allergies: string;
            medications: string;
            pastMedicalHistory: string;
            lastOralIntake: string;
            eventsPrior: string;
            medsTakenToHospital?: boolean;
        };
        allergies: string;
        medications: string;
        pastMedicalHistory: string;
        socialHistory?: string;
        familyHistory?: string;
    };
    assessment: {
        primary: PrimarySurvey;
        clinicalNarrative: string;
        globalAssessment?: {
            primaryComplaint?: string;
            symptomEvolution?: string;
            habitus?: string;
            distressLevel?: string;
            posturePosition?: string;
            environmentObservations?: string;
            sceneNotes?: string;
        };
        interaction?: AssessmentInteraction;
        neuro: NeuroAssessment;
        cardiac?: {
            chestPainPresent: boolean;
            heartScore?: string;
            jrcalcAcsFlags?: string;
            heartSounds?: string;
            jvp?: string;
            oedema?: string;
            chestPainCharacter?: string;
            chestPainRadiation?: string;
            acsRiskLevel?: 'Low' | 'Moderate' | 'High';
            stemiSuspected?: boolean;
            heartFailureSigns?: string;
            ecg?: { 
                leadMode?: '' | '3-Lead' | '12-Lead';
                rhythm: string; 
                rate: string; 
                time: string; 
                stChanges: boolean; 
                lbbbPresent?: boolean;
                fifteenLeadPerformed?: boolean;
                fifteenLeadFindings?: string;
                stDetails?: { type: 'Elevation' | 'Depression' | 'None'; leads: string; };
                twelveLeadNotes?: string 
            };
            arrhythmiaRedFlags?: string;
            syncopeRedFlags?: string;
            posturalBpDrop?: string;
            socrates?: { site: string; onset: string; character: string; radiation: string; associations: string; timeCourse: string; exacerbatingRelieving: string; severity: string; };
            wellsCriteria?: string[];
            wellsScore?: number;
        };
        respiratory?: {
            cough: string;
            sputumColor: string;
            peakFlowPre: string;
            peakFlowPost: string;
            predictedPeakFlow?: string;
            etco2?: string;
            speechTolerance?: string;
            airEntryL: string;
            airEntryR: string;
            addedSoundsL: string;
            addedSoundsR: string;
            accessoryMuscleUse: boolean;
            accessoryMuscleDetails?: string; 
            trachealTug?: boolean; 
            nebulisersGiven?: boolean;
            cpapConsidered?: boolean;
            cpapContraindications?: string;
            asthmaCopdSeverity?: string;
            wellsPeCriteria?: {
                clinicalSignsDvt?: boolean;
                peMostLikely?: boolean;
                heartRateOver100?: boolean;
                immobilisation?: boolean;
                previousPeDvt?: boolean;
                haemoptysis?: boolean;
                malignancy?: boolean;
                surgery?: boolean;
            };
            percCriteriaChecks?: {
                ageOver50?: boolean;
                heartRateOver100?: boolean;
                spo2Below95?: boolean;
                unilateralSwelling?: boolean;
                haemoptysis?: boolean;
                recentSurgeryOrTrauma?: boolean;
                previousPeDvt?: boolean;
                oestrogenUse?: boolean;
            };
            wellsPeScore?: string;
            percCriteria?: string;
            wellsDvtScore?: string;
            history?: string;
        };
        gastrointestinal?: {
            abdominalPain: boolean;
            distension: string;
            palpation: string;
            bowelSounds: string;
            fastScan?: string;
            urineOutput: string;
            vomitDescription: string;
            stoolDescription: string;
            painLocation: string;
            lastMeal?: string;
            lastBowelMovement?: string;
            lastVomit?: string; 
            nausea: boolean; 
            vomiting: boolean; 
            diarrhoea: boolean; 
            fluidIntake?: string; 
            quadrants?: { ruq: string; luq: string; rlq: string; llq: string }; 
            nineRegionFindings?: string;
            murphysSign?: boolean;
            rovsingMcburneySign?: boolean;
            pulsatileMass?: boolean;
            kehrsSign?: boolean;
            bristolStoolType?: string;
            melaena?: boolean;
            prBleeding?: boolean;
            haematemesisType?: string;
            projectileVomiting?: boolean;
            flankPain?: boolean;
            dysuria?: boolean;
            urinaryFrequency?: boolean;
            urineCharacteristics?: string;
            lastVoidedAt?: string;
            retentionSuspected?: boolean;
            palpableBladder?: boolean;
        };
        obsGynae?: {
            pregnant: boolean;
            gestationWeeks?: string;
            gravida?: string;
            para?: string;
            labourStage?: 'Latent' | 'Active' | 'Transition' | 'Second Stage' | 'Third Stage' | 'Postpartum';
            contractions?: string;
            contractionFrequency?: string;
            contractionDuration?: string;
            presentation?: 'Cephalic' | 'Breech' | 'Transverse' | 'Unknown';
            babyCount?: number;
            deliveryMode?: 'Spontaneous' | 'Assisted' | 'Caesarean' | 'Water' | 'Unknown';
            placentaDelivered?: boolean;
            cordClamped?: boolean;
            newbornsDelivered?: number;
            deliveryComplications?: string;
            membranesRuptured?: boolean;
            fluidColour?: 'Clear' | 'Meconium' | 'Blood' | 'Unknown';
            estimatedBloodLoss?: string;
            bleeding?: boolean;
            bleedAmount?: string;
            foetalMovements?: boolean;
            apgarOneMinute?: number;
            apgarFiveMinutes?: number;
            newbornCondition?: string;
            resuscitationRequired?: boolean;
            resuscitationDetails?: string;
            knownComplications?: string;
            membranesRupturedAt?: string;
            contractionFrequencyMinutes?: string;
            contractionDurationSeconds?: string;
            cordProlapseVisible?: boolean;
            birthHeadDeliveredAt?: string;
            birthBodyDeliveredAt?: string;
            placentaDeliveredAt?: string;
            preEclampsiaBpOver14090?: boolean;
            preEclampsiaHeadache?: boolean;
            preEclampsiaVisualDisturbance?: boolean;
            preEclampsiaEpigastricPain?: boolean;
            preEclampsiaHyperreflexia?: boolean;
            notes?: string;
        };
        musculoskeletal?: {
            notes?: string;
            weightBearingStatus?: string;
            distalNeurovascularStatus?: string;
            splintageProvided?: string;
            cspineClearanceReason?: string;
            cspineNexusClear?: boolean;
            canadianCSpineClear?: boolean;
            ottawaAnkleClear?: boolean;
            ottawaFootClear?: boolean;
            ottawaKneeClear?: boolean;
            palpationFindings?: string;
            distalPulsePre?: boolean;
            distalSensationPre?: boolean;
            distalMotorPre?: boolean;
            distalPulsePost?: boolean;
            distalSensationPost?: boolean;
            distalMotorPost?: boolean;
            woundLengthCm?: string;
            woundDepth?: string;
            woundContamination?: string;
            activeBleeding?: boolean;
            haemostasisAchieved?: boolean;
        };
        mentalHealth?: MentalHealthAssessment;
        traumaTriage?: TraumaTriageResult;
        sepsis?: {
            redFlags: string[];
            suspectedSource?: string[];
            riskFactors: string[];
            screeningTrigger?: boolean;
            outcome?: string;
            sepsisSixActions?: string[];
            escalationTime?: string;
            lactateValue?: string;
        };
        wounds?: WoundAssessment[];
        burns?: { estimatedPercentage: string; depth: string; site?: string; notes?: string };
        cfsScore?: number;
        mobility?: { preMorbidMobility: string; currentMobility: string; transferAbility: string; aidsUsed: string };
        social?: { 
            livingStatus: string; 
            carers: boolean;
            supportDetails: string;
            accessKeys?: boolean;
        };
        monitorIntegration?: {
            source?: 'Manual Entry' | 'Monitor Import' | 'Mixed';
            deviceType?: string;
            deviceId?: string;
            importedAt?: string;
            autoPopulatedFields?: string[];
            trendSummary?: string;
        };
        falls?: { 
            historyOfFalls: boolean; 
            anticoagulants?: string; 
            unsteadyWalk: boolean;
            visualImpairment?: boolean;
            alteredMentalState?: boolean;
            medications?: boolean;
            timeOfFall?: string;
            lastSeenWell?: string;
            mobileOnFloor?: boolean;
            timeFound?: string;
            timeRemovedFromFloor?: string;
            removedBy?: string;
            injurySustained?: boolean;
            causeCategory?: 'Environmental' | 'Medical' | 'Mixed' | '';
            causeDetails?: string;
            timeline?: string;
            floorTimeMinutes?: string;
            likelyCause?: 'Environmental' | 'Medical' | 'Mixed' | '';
            rhabdomyolysisRisk?: boolean;
            anaphylaxisSuspected?: boolean;
            anaphylaxisCriteria?: string;
            respectStatus?: string;
        };
        paediatrics?: {
            wetflagWeightKg?: string;
            wetflagAgeYears?: string;
            wetflagAgeMonths?: string;
            wetflagResult?: string;
            wetflagAutoCalc?: string;
            assessmentTriangleConcern?: boolean;
            patAppearance?: string;
            patWorkBreathing?: string;
            patCirculationSkin?: string;
            popsScore?: string;
            feverRedFlags?: string;
            feverAmberFlags?: string;
            safeguardingNotes?: string;
        };
        minorInjuryAssessment?: string;
    };
    treatments: {
        drugs: DrugAdministration[];
        procedures: Procedure[];
        minorTreatment?: string;
        role?: {
            verificationMethod?: 'VOD' | 'ROLE';
            timeVerified: string;
            verifiedBy: string;
            arrestWitnessed: boolean;
            bystanderCPR: boolean;
            dnacprAvailable: boolean; 
            downTimeMinutes?: number;
            totalShocks?: number;
            criteriaMet: string[];
            resusSummary: string; 
            observationMinutes?: number;
            coronerReferralRequired?: boolean;
            coronerReference?: string;
        };
        resusLog?: ResusEvent[];
    };
    clinicalDecision: {
        workingImpression: string;
        differentialDiagnosis: string;
        managementPlan: string;
        decisionMaker?: {
            type: 'Face-to-Face' | 'Remote';
            role: string;
        };
        finalDisposition: string;
        destinationTrust?: string;
        destinationRegion?: string;
        destinationHospital?: string;
        destinationDepartment?: string;
        gpPractice?: string;
        gpName?: string;
        gpCallTime?: string;
        gpRefNumber?: string;
        pathwayData?: {
            pathwayName: string;
            criteriaMet: string[];
            outcome: string;
        };
    };
    governance: {
        capacity: {
            status: string;
            stage1?: { impairment: boolean | undefined; nexus: boolean | undefined };
            stage2Functional?: { understand: boolean; retain: boolean; weigh: boolean; communicate: boolean };
            bestInterestsAgreed?: boolean;
        };
        safeguarding: {
            concerns: boolean;
            category: string;
            type?: string[];
            details: string;
            referralMade: boolean;
            referralReference: string;
        };
        refusal: {
            isRefusal: boolean;
            type: string[];
            details: string;
            risksExplained: boolean;
            capacityConfirmed: boolean;
            alternativesOffered: boolean;
            worseningAdviceGiven: boolean;
            patientRefusedToSign: boolean;
            patientSignature?: string;
            patientSigTime?: string;
            witnessName?: string;
            witnessSignature?: string;
            witnessSigTime?: string;
            staffSignature?: string;
            staffSigTime?: string;
        };
        worseningAdviceDetails?: string;
    };
    handover: {
        handoverType: string;
        receivingName: string;
        receivingPin: string;
        receivingTime: string;
        sbar?: string;
        atmist?: string;
        media?: MediaAttachment[];
        clinicianSignature?: string;
        clinicianSigTime?: string;
        receivingClinicianSignature?: string;
        receivingSigTime?: string;
        patientSignature?: string;
        patientSigTime?: string;
        patientRefusedToSign?: boolean;
        digitalToken?: string;
        portalTokenId?: string;
        portalTokenGeneratedAt?: string;
        portalTokenExpiresAt?: string;
        portalTokenStatus?: 'Active' | 'Viewed' | 'Acknowledged' | 'Expired' | 'Revoked';
        portalViewedAt?: string;
        portalAcknowledgedAt?: string;
        portalVerifierName?: string;
        portalVerifierSite?: string;
        portalVerifierDepartment?: string;
        portalIdentityVerifiedAt?: string;
        completionMethod?: 'DirectRecipient' | 'DigitalPortal' | 'Mixed' | 'NotRequired';
        completionVerifiedAt?: string;
    };
    vitals: VitalsEntry[];
    injuries: InjuryMark[]; 
    logs: any[];
    auditTrail?: AuditTrailEntry[];
    linkedRecords?: LinkedRecord[];
    birthEpisode?: BirthEpisode;
    deathVerification?: DeathVerification;
    bodyMapImage?: string;
    accessMapImage?: string;
    pdfUrl?: string;
    locked?: boolean;
    linkage?: {
        incidentId?: string;
        assignmentId?: string;
        unitId?: string;
        unitCallSign?: string;
        fleetVehicleId?: string;
        medicalKitIds?: string[];
        source: 'Manual' | 'Dispatch';
    };
    dispatchSync?: {
        mode: 'always' | 'untilManualEdit';
        locked: boolean;
        lockedAt?: string;
        lastSyncedAt?: string;
    };
    immutableVersion?: {
        latestVersionId: string;
        latestVersionAt: string;
        latestHash: string;
    };
}

// ─── Dispatch / CAD Domain ────────────────────────────────────────────────────

export enum DispatchStatus {
    Pending = 'Pending',
    Assigned = 'Assigned',
    Enroute = 'Enroute',
    OnScene = 'OnScene',
    Transporting = 'Transporting',
    AtHospital = 'AtHospital',
    Cleared = 'Cleared',
    Cancelled = 'Cancelled',
}

export interface DispatchUnit {
    id: string;
    tenantId: string;
    callSign: string;
    vehicleId?: string;
    crewIds: string[];
    crewNames?: string[];
    status: DispatchStatus;
    currentIncidentId?: string;
    lastUpdated: string;
    location?: { lat: number; lng: number };
}

export interface DispatchIncident {
    id: string;
    tenantId: string;
    incidentNumber: string;
    priority: 'P1' | 'P2' | 'P3' | 'P4';
    category: string;
    description: string;
    address: string;
    location?: { lat: number; lng: number };
    status: DispatchStatus;
    assignedUnitIds: string[];
    linkedEprfId?: string;
    dispatchSyncMode?: 'always' | 'untilManualEdit';
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    closedAt?: string;
    notes?: string;
}

export interface Assignment {
    id: string;
    tenantId: string;
    incidentId: string;
    unitId: string;
    assignedAt: string;
    assignedBy: string;
    status: DispatchStatus;
    acknowledgedAt?: string;
    onSceneAt?: string;
    clearedAt?: string;
    eprfId?: string;
}

export interface DispatchEvent {
    id: string;
    tenantId: string;
    incidentId: string;
    assignmentId?: string;
    type: 'Assigned' | 'Acknowledged' | 'Enroute' | 'OnScene' | 'Transporting' | 'Cleared' | 'Cancelled' | 'Note' | 'StatusChange';
    message: string;
    userId: string;
    userName?: string;
    createdAt: string;
    metadata?: Record<string, any>;
}

// ── Device Management ─────────────────────────────────────────────────────

/** DeviceType drives the sidebar layout and route permissions for company-owned vs personal devices. */
export type DeviceType = 'ambulance_tablet' | 'base_station' | 'byod';

export interface RegisteredDevice {
    deviceId: string;
    type: DeviceType;
    /** Human label set by admin, e.g. "Ambulance 3 — Unit 704" */
    label: string;
    tenantId: string;
    registeredBy: string;
    registeredByName: string;
    registeredAt: string;
    lastActiveAt?: string;
}

export type WorkspaceIntent =
    | 'dispatch_control'
    | 'crew_mdt'
    | 'personal_portal'
    | 'admin_setup'
    | 'hospital_portal'
    | 'wallboard';

// ── Hospital Handover Portal ──────────────────────────────────────────────

/** Snapshot of clinical data embedded in a handover token (avoids a second Firestore read from hospital side). */
export interface HandoverEPRFSnapshot {
    patientName: string;
    dob: string;
    gender: string;
    nhsNumber?: string;
    chiefComplaint: string;
    finalDisposition: string;
    sbar?: string;
    atmist?: string;
    latestVitals?: VitalsEntry;
    drugs: DrugAdministration[];
    procedures: Procedure[];
    incidentNumber: string;
    callReceived: string;
    onScene?: string;
    arrivedHospital?: string;
    crewLead: string;
    crewCallSign?: string;
    allergies?: string;
    medications?: string;
    pastMedicalHistory?: string;
}

/** Time-limited token written to Firestore `handoverTokens/{id}` and encoded in a QR code for hospital staff. */
export interface HandoverToken {
    id: string;
    ePRFId: string;
    tenantId: string;
    tenantName: string;
    destinationHospital: string;
    destinationDepartment?: string;
    generatedAt: string;
    /** ISO timestamp — 6 hours after generatedAt */
    expiresAt: string;
    status: 'Active' | 'Viewed' | 'Acknowledged' | 'Expired' | 'Revoked';
    viewedAt?: string;
    acknowledgedAt?: string;
    revokedAt?: string;
    revokedReason?: string;
    trustMode?: 'standard' | 'strict_two_party';
    verifiedByName?: string;
    verifiedSite?: string;
    verifiedDepartment?: string;
    identityVerifiedAt?: string;
    ePRFSnapshot: HandoverEPRFSnapshot;
}

// ── Crew Management ──────────────────────────────────────────────────────────

export enum CrewStatus {
    Available = 'Available',
    Mobile = 'Mobile',
    OnScene = 'OnScene',
    Transporting = 'Transporting',
    AtHospital = 'AtHospital',
    OnBreak = 'OnBreak',
    OffDuty = 'OffDuty',
    Unavailable = 'Unavailable',
}

export interface CrewShift {
    id: string;
    tenantId: string;
    crewMemberId: string;
    crewMemberName: string;
    crewMemberRole: Role;
    additionalCrewMembers?: Array<{
        userId: string;
        name: string;
        role: Role;
        employeeId?: string;
    }>;
    vehicleId?: string;
    vehicleCallSign?: string;
    shiftStart: string;  // ISO timestamp
    plannedShiftStart?: string;
    plannedShiftEnd?: string;
    odometerStart?: number;
    odometerEnd?: number;
    vdiCompleted?: boolean;
    vdiNotes?: string;
    shiftEnd?: string;
    status: CrewStatus;
    currentIncidentId?: string;
    currentAssignmentId?: string;
    breaks: BreakRecord[];
    handovers: CrewHandoverEvent[];
    totalBreakMinutes?: number;
    totalOnSceneMinutes?: number;
    signedOffBy?: string;
    signedOffAt?: string;
    signOffReason?: string;
    reconciliation?: {
        controlledDrugCheckCompleted: boolean;
        assetCheckCompleted: boolean;
        discrepancies: Array<{
            id: string;
            type: 'ControlledDrug' | 'Asset';
            referenceId?: string;
            description: string;
            severity: 'Low' | 'Medium' | 'High';
            resolved?: boolean;
            resolutionNotes?: string;
        }>;
        notes?: string;
        override?: {
            enabled: boolean;
            reason?: string;
            approvedByUid?: string;
            approvedByName?: string;
        };
        completedAt?: string;
        completedByUid?: string;
        completedByName?: string;
        summary?: {
            discrepancyCount: number;
            unresolvedCount: number;
            overridden: boolean;
        };
    };
}

export interface BreakRecord {
    id: string;
    startTime: string;  // ISO timestamp
    endTime?: string;
    durationMinutes?: number;
    type: 'Meal' | 'Rest' | 'Admin' | 'Other';
    reason?: string;
}

export interface CrewHandoverEvent {
    id: string;
    timestamp: string;  // ISO timestamp
    fromCrewMemberId: string;
    fromCrewMemberName: string;
    toCrewMemberId: string;
    toCrewMemberName: string;
    role: Role;  // Which role being handed over
    currentIncidentId?: string;
    details?: string;
}

export interface OccupancyRecord {
    id: string;
    tenantId: string;
    vehicleId: string;
    timestamp: string;  // ISO timestamp
    crewCount: number;
    patientCount: number;
    totalOccupancy: number;
    maxCapacity: number;  // Total crew + patient capacity
    canAcceptMorePatients: boolean;
}

export interface CFRIncidentReport {
    id: string;
    tenantId: string;
    reportingCFRId: string;
    reportingCFRName: string;
    incidentTime: string;  // ISO timestamp
    location: string;
    coordinates?: { lat: number; lng: number };
    
    patientCondition: {
        conscious: boolean;
        breathing: boolean;
        abcdeAssessment?: string;
    };
    
    interventionsProvided: {
        bls: boolean;
        defibrillation: boolean;
        recovery_position: boolean;
        oxygen: boolean;
        first_aid: boolean;
        notes?: string;
    };
    
    linkedEprfId?: string;
    linkedNhsCrewUserId?: string;
    linkedNhsCrewName?: string;
    submittedAt: string;
}

export interface MonitorDeviceData {
    id: string;
    tenantId: string;
    eprfId: string;
    deviceType: 'Zoll' | 'Corpulse' | 'Lifepack' | 'Philips' | 'Other';
    deviceSerialNumber?: string;
    
    ecg: {
        rhythm: 'Sinus' | 'Afib' | 'VTach' | 'VFib' | 'Asystole' | 'PEA' | 'Other';
        rate?: number;
        stChanges?: boolean;
        stLocation?: string;
        waveformUrl?: string;  // PNG or DICOM file stored in Firebase Storage
        capturedAt: string;
    };
    
    defibrillation?: {
        shocked: boolean;
        numberOfShocks?: number;
        shocks?: {
            timestamp: string;
            energyJoules: number;
            rhythmBefore: string;
            rhythmAfter: string;
        }[];
    };
    
    capnography?: {
        etco2: number;
        waveformUrl?: string;
        capturedAt: string;
    };
    
    crpFeedback?: {
        compressionDepth: number;  // mm
        compressionRate: number;  // per minute
        ventilationRate?: number;
        noFlowTime?: number;  // seconds
    };
    
    syncedToEprfAt?: string;
}

export interface MotionDetectionState {
    isMoving: boolean;
    accelerometerData?: {
        timestamp: string;
        x: number;
        y: number;
        z: number;
    };
    gpsSpeed?: number;  // km/h
    lastUpdateTime: string;
}
